import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { IstPipe } from '../../core/pipes/ist.pipe';
import { ApiService } from '../../core/services/api.service';
import { InvoiceDto } from '../../core/models/billing.model';
import { NetworkGuardService } from '../../core/services/network-guard.service';
import { DialogService } from '../../core/services/dialog.service';

@Component({
    selector: 'app-billing',
    imports: [CommonModule, RouterModule, IstPipe],
    templateUrl: './billing.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./billing.component.scss']
})
export class BillingComponent implements OnInit {
  invoice: InvoiceDto | null = null;

  /** Date of birth is optional at registration; with none the server sends the empty date, which printed as 01/01/0001. */
  get hasDateOfBirth(): boolean {
    return !!this.invoice?.dateOfBirth && new Date(this.invoice.dateOfBirth).getFullYear() > 1900;
  }
  loading = false;
  error = '';
  billId: number = 0;

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private networkGuard: NetworkGuardService,
    private dialog: DialogService
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.billId = +params['billId'];   // Route param is :billId
      if (this.billId) {
        this.loadInvoice();
      }
    });
  }

  loadInvoice() {
    this.loading = true;
    this.apiService.getInvoiceDetails(this.billId).subscribe({
      next: (invoice) => {
        this.invoice = invoice;
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load invoice. Please try again.';
        this.loading = false;
      }
    });
  }

  /** Display-only: hospital monogram for the invoice logo tile (e.g. "Childrens Hospital" → "CH"). */
  get hospitalInitials(): string {
    const words = (this.invoice?.hospitalName || '').trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('') || 'H';
  }

  /** Display-only: readable line-item category for the muted sub-line (board: "Lab · …", "Pharmacy · …"). */
  categoryLabel(category?: string): string {
    switch ((category || '').trim()) {
      case '': return '';
      case 'BedCharges': return 'Bed charges';
      case 'OP': return 'OP';
      default: return category!.trim();
    }
  }

  /** Toolbar "Back" — returns to wherever the invoice was opened from. */
  goBack() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.assign('/');
    }
  }

  // Print invoice using browser print dialog (same pattern as discharge summary)
  printInvoice() {
    window.print();
  }

  sendInvoice(sms: boolean = false, email: boolean = true) {
    if (!this.invoice) return;
    this.apiService.sendInvoice(this.invoice.billId, sms, email).subscribe({
      // The backend reports whether delivery actually happened — SMS/Email is a stub until
      // Twilio/SendGrid is wired up. Surface that honest message instead of always claiming
      // "sent successfully" (which would tell the cashier an invoice went out when it didn't).
      next: (res: any) => this.dialog.alert(res?.message || 'Invoice sent successfully'),
      error: () => this.dialog.alert('Failed to send invoice', { tone: 'danger' })
    });
  }
}
